import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import theme from '~/theme';
import { mqFromDesktop } from '~/variables/breakpoints';

type StyleObject = SystemStyleObject<Theme>;

// static styles (legacy)
const sidebarStyle = {
  drawerPaperRTL: {
    [theme.breakpoints?.up('md')]: {
      left: 'auto !important',
      right: '0 !important',
    },
    [theme.breakpoints?.down('md')]: {
      left: '0  !important',
      right: 'auto !important',
    },
  },
  drawerPaper: {
    border: 'none',
    bottom: '0',
    left: '0',
    zIndex: '1032',
    transitionProperty: 'top, bottom, width',
    transitionDuration: '.2s, .2s, .35s',
    transitionTimingFunction: 'linear, linear, ease',
    width: theme.defaultStyles?.drawerWidth,
    [theme.breakpoints?.up('md')]: {
      width: theme.defaultStyles?.drawerWidth,
      height: '100%',
    },
    position: 'relative',
    '@media (max-width: 600px)': {
      width: '100%',
      ...theme.defaultStyles?.boxShadow,
      position: 'fixed',
      display: 'block',
      top: '50px',
      height: '100vh',
      right: '0',
      left: 'auto',
      zIndex: '1032',
      visibility: 'visible',
      overflowY: 'visible',
      borderTop: 'none',
      textAlign: 'left',
      paddingTop: '0',
      paddingRight: '0px',
      paddingLeft: '0',
      transform: `translate3d(${theme.defaultStyles?.drawerWidth}px, 0, 0)`,
      transition: theme.defaultStyles?.transition,
      backgroundColor: theme.palette.dianomiGrey.main,
    },
    '&:before,&:after': {
      position: 'absolute',
      zIndex: '3',
      width: '100%',
      height: '100%',
      content: '""',
      display: 'block',
      top: '0',
    },
  },
  blackBackground: {
    color: theme.palette?.secondary.main,
    background: theme.palette?.primary.main,
  },
  dianomGreyBackground: {
    backgroundColor: theme.palette.dianomiGrey.main,
    color: theme.palette?.primary.main,
  },
  blueBackground: {
    color: theme.palette?.secondary.main,
    '&:after': {
      opacity: '.93',
    },
  },
  whiteBackground: {
    color: theme.palette?.grey[900],
    '&:after': {
      background: theme.palette?.secondary.main,
      opacity: '.93',
    },
  },
  whiteAfter: {
    '&:after': {
      backgroundColor: 'hsla(0,0%,71%,.3) !important',
    },
  },
  drawerPaperMini: {
    '@media (min-width: 601px)': {
      width: `${theme.defaultStyles?.drawerWidthMini}px!important`,
    },
  },
  img: {
    width: '100%',
    verticalAlign: 'middle',
    border: '0',
    padding: '10px',
  },
  background: {
    position: 'absolute',
    zIndex: '1',
    height: '100%',
    width: '100%',
    display: 'block',
    top: '0',
    left: '0',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    transition: 'all 300ms linear',
  },
  collapseList: {
    marginTop: '0',
  },
  caretActive: {
    transform: 'rotate(180deg)',
  },
  sidebarWrapper: {
    position: 'fixed',
    height: 'calc(100vh)',
    overflow: 'auto',
    width: '210px',
    zIndex: '4',
    overflowScrolling: 'touch',
    transition: 'width 250ms ease-out',
    color: 'inherit',
    paddingBottom: '30px',
    marginTop: '0',
    backgroundColor: theme.palette.dianomiGrey.main,
    borderRight: '1px solid #e0e0e0',
    [mqFromDesktop]: {
      maxWidth: '210px',
    },
    [theme.breakpoints?.down('sm')]: {
      height: '100%',
      width: '100%',
    },
  },
  mobileMenu: {
    '@media (max-width: 600px)': {
      display: 'none',
    },
  },
} as const;

// mobile styles
export const mobileStyles = {
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    py: 1.5,
    px: 2,
    color: 'text.primary',
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  drawer: (themeObj: Theme) => ({
    '& .MuiDrawer-paper': {
      ...sidebarStyle.drawerPaper,
      backgroundColor: theme.palette.dianomiGrey.main,
      color: themeObj.palette?.primary.main,
    },
  }),
  sidebarWrapper: {
    ...sidebarStyle.sidebarWrapper,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    pb: 10,
    backgroundColor: theme.palette.dianomiGrey.main,
  },
  divider: {
    my: 2,
    mx: 2,
    borderColor: 'rgba(0, 0, 0, 0.12)',
  },
  accountHeader: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'text.secondary',
    letterSpacing: '0.08em',
  },
} as const;

// shared desktop styles (no changes with sidebar collapsed/expanded state)
const desktopSharedStyles = {
  scrollableArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: theme.palette.dianomiGrey.main,
    '&::-webkit-scrollbar': {
      width: '2px',
      WebkitAppearance: 'none',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.dianomiGrey.main,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.25)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.secondary.main} ${theme.palette.dianomiGrey.main}`,
  },
  list: {
    backgroundColor: theme.palette.dianomiGrey.main,
  },
  footer: {
    flexShrink: 0,
    width: '100%',
    zIndex: 4000,
    backgroundColor: theme.palette.dianomiGrey.main,
  },
  logoLink: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 300ms linear',
    opacity: '1',
    transform: 'translate3d(0px, 0, 0)',
    textTransform: 'none' as const,
    fontSize: '18px',
    whiteSpace: 'nowrap' as const,
    fontWeight: '400',
    lineHeight: '30px',
    overflow: 'hidden' as const,
    color: 'white',
    gap: '10px',
    fontFamily: 'Nexa !important',
  },
};

// base wrapper styles for desktop
const desktopWrapperBase = {
  backgroundColor: theme.palette.dianomiGrey.main,
  paddingRight: '0px',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  paddingBottom: 0,
  justifyContent: 'space-between',
  overflow: 'visible',
};

// desktop styles - with expanded and collapsed variants
export const desktopStyles = {
  // shared styles
  ...desktopSharedStyles,

  // expanded sidebar variant
  expanded: {
    drawer: {
      '& .MuiDrawer-paper': {
        ...sidebarStyle.drawerPaper,
        ...sidebarStyle.blackBackground,
        overflow: 'visible',
        transition: 'width 250ms ease-out',
      },
    },
    sidebarWrapper: {
      ...sidebarStyle.sidebarWrapper,
      ...desktopWrapperBase,
    },
    header: {
      maxWidth: '210px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: theme.palette.dianomiGrey.main,
      flexShrink: 0,
      margin: '10px',
      paddingBottom: '10px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    logoContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '10px',
      marginBottom: '20px',
      width: '100%',
      marginLeft: '15px',
    },
    logoImg: {
      width: '125px',
      transition: 'all 300ms linear',
      verticalAlign: 'middle',
      border: '0',
      position: 'relative' as const,
      marginRight: '18px',
      filter: 'invert(1)',
    },
    menuIconButton: {
      borderRadius: '4px',
      padding: '4px',
      '&:hover': {
        backgroundColor: 'secondary.main',
      },
    },
    clientSelectorWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0px 10px',
    },
  },

  // collapsed sidebar variant
  collapsed: {
    drawer: {
      '& .MuiDrawer-paper': {
        ...sidebarStyle.drawerPaper,
        ...sidebarStyle.drawerPaperMini,
        ...sidebarStyle.blackBackground,
        overflow: 'visible',
        transition: 'width 250ms ease-out',
      },
    },
    sidebarWrapper: {
      ...sidebarStyle.sidebarWrapper,
      ...sidebarStyle.drawerPaperMini,
      ...desktopWrapperBase,
    },
    header: {
      maxWidth: '220px',
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: theme.palette.dianomiGrey.main,
      flexShrink: 0,
      margin: '0px 10px 10px',
      paddingTop: '10px',
      paddingBottom: '10px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    logoContainer: {
      display: 'none', // Not used in collapsed mode
    },
    logoImg: {
      width: '50px',
      transition: 'all 300ms linear',
      verticalAlign: 'middle',
      border: '0',
      position: 'relative' as const,
      marginLeft: '0px',
      marginRight: '0px',
      filter: 'invert(1)',
    },
    menuIconButton: {
      borderRadius: '4px',
      padding: '4px',
      '&:hover': {
        backgroundColor: 'secondary.main',
      },
    },
    clientSelectorWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0px',
    },
  },

  // toggle button positioned straddling the sidebar edge
  toggleButtonContainer: (effectiveSidebarOpen: boolean) => ({
    position: 'fixed',
    left: effectiveSidebarOpen ? '192px' : '62px', // sidebar width - half button width
    top: effectiveSidebarOpen ? '14px' : '20px', // align with logo/client icon
    zIndex: 1100,
    transition: 'left 250ms ease-out, top 250ms ease-out',
  }),
  toggleButton: (effectiveSidebarOpen: boolean) => ({
    backgroundColor: theme.palette.dianomiGrey.main,
    borderRadius: '4px',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      borderColor: theme.palette.dianomiGreen.main,
    },
  }),
} as const;

export const logoLinkStyle = desktopSharedStyles.logoLink;

export const authLinkItemSx: StyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 1.5,
  px: 2,
  color: 'text.primary',
  textDecoration: 'none',
  '&:hover': {
    color: 'primary.main',
    backgroundColor: 'secondary.main',
  },
  ' img': {
    width: '20px',
    marginLeft: '10px',
    marginRight: '0px',
  },
};

export default sidebarStyle;
